// @flow
import client from '../../client'
import { config } from '../../config'
import APIError from '../../client/APIError'
import format from '../../utils/format'

// Types
export type CancelResult = {
  cancelled: boolean,
  refunded: boolean,
}
export type RequestData = {
  id: string,
  ondemand?: boolean,
  issue_coupon?: boolean,
}

// Constants
export const RESERVATION_ENDPOINT = '/reservations/{0}/'
export const ONDEMAND_RESERVATION_ENDPOINT =
  '/ondemand/reservations/{0}/cancel/'

/**
 * Cancel a prebook reservation
 * @param  {Object} data Reservation information
 * @param  {string} data.id Reservation ID
 * @return {Promise|Error} A resolved Promise with the reservation cancel confirmation or Rejected with an Error
 */
export const cancelPrebook = (data: RequestData): Promise<CancelResult> => {
  const endpoint = format(RESERVATION_ENDPOINT, data.id)
  const query = { issue_coupon: !!data.issue_coupon }

  return client
    .delete(endpoint, { query, baseUrl: config.BASE_URL_RESERVATIONS })
    .then((response) => ({
      cancelled: Boolean(response.cancelled),
      refunded: Boolean(response.refunded),
    }))
}

/**
 * Cancel a ondemand reservation
 * @param  {Object} data Reservation information
 * @param  {string} data.id Reservation ID
 * @return {Promise|Error} A resolved Promise with the reservation cancel confirmation or Rejected with an Error
 */
export const cancelOndemand = (data: RequestData): Promise<CancelResult> => {
  const endpoint = format(ONDEMAND_RESERVATION_ENDPOINT, data.id)

  return client.post(endpoint).then((response) => {
    if (response.cancelled) return response
    if (!response.cancellation_token) {
      throw new APIError(
        "Can't cancel the reservation, please contact support",
        'failed',
        400
      )
    }

    const body = { cancellation_token: response.cancellation_token }
    return client.post(endpoint, { body }).then(() => ({
      cancelled: true,
      refunded: true,
    }))
  })
}

const cancel = (data: RequestData): Promise<CancelResult> =>
  data.ondemand ? cancelOndemand(data) : cancelPrebook(data)

export default cancel
